import React, { useEffect, useState } from "react";

import {
  Button,
  Divider,
  Dropdown,
  Menu,
  message,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { arrayMoveImmutable } from "array-move";
import PropTypes from "prop-types";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";

import { MenuOutlined } from "@ant-design/icons";

import { getRefernEarnProducts, reorderRefernEarnProduct } from "../Pages/api";
import RefernEarnAddEditProductModal from "./RefernEarnAddEditProductModal";
import RefernEarnPartnerSalesAnalyticsExpand from "./RefernEarnPartnerSalesAnalyticsExpand";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};
const DragHandle = SortableHandle(() => (
  <MenuOutlined style={{ cursor: "grab", color: "#999" }} />
));

const SortableItem = SortableElement((props) => <tr {...props} />);

const SortableBody = SortableContainer((props) => <tbody {...props} />);

const RefernEarnPartnerDetailsExpand = ({ userDetails, setCurrentScreen }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [mode, setMode] = useState("add");
  const [product, setProduct] = useState(null);
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetRefernEarnPartnerProducts = async () => {
    setLoading(true);
    try {
      const res = await getRefernEarnProducts({
        user: userDetails?._id,
      });

      if (res?.code === 200) {
        setData(res.result.map((item, index) => ({ ...item, index })));
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleReorderRefernEarnPartnerProducts = async (newData) => {
    setLoading(true);
    try {
      const res = await reorderRefernEarnProduct({
        order: newData.map((item) => item._id),
        user: userDetails?._id,
      });

      if (res?.code === 200) {
        setData(newData);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        data.slice(),
        oldIndex,
        newIndex
      ).filter((el) => !!el);
      handleReorderRefernEarnPartnerProducts(newData);
    }
  };

  useEffect(() => {
    if (userDetails) {
      handleGetRefernEarnPartnerProducts();
    }
  }, [userDetails]);

  const columns = [
    {
      title: "",
      dataIndex: "sort",
      width: 30,
      className: "drag-visible",
      render: () => <DragHandle />,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      render: (value, record) => (
        <Space direction="vertical">
          <Typography.Text>{value}</Typography.Text>

          {record.productLink ? (
            <a
              href={record.productLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#1890ff", fontSize: 12 }}
            >
              Product Link
            </a>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Value",
      dataIndex: "totalProductPrice",
      key: "totalProductPrice",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.totalProductPrice - b.totalProductPrice,
      render: (value) =>
        value > 0 ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16 }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>Free</span>
        ),
    },
    {
      title: "Revenue Share",
      dataIndex: "revenueShare",
      key: "revenueShare",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.revenueShare - b.revenueShare,
      render: (value) => (value != null ? `${value}%` : "-"),
    },
    {
      title: "Is Exclusive",
      dataIndex: "isExclusive",
      key: "isExclusive",
      filters: [
        {
          text: "Yes",
          value: true,
        },
        {
          text: "No",
          value: false,
        },
      ],
      onFilter: (value, record) => record.isExclusive === value,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Listing Status",
      dataIndex: "status",
      key: "status",
      filters: [
        {
          text: "Listed",
          value: "listed",
        },
        {
          text: "Hidden",
          value: "hidden",
        },
        {
          text: "Expired",
          value: "expired",
        },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
      render: (value) => (
        <Tag
          color={
            // eslint-disable-next-line no-nested-ternary
            value === "listed" ? "green" : value === "hidden" ? "yellow" : "red"
          }
          style={{ textTransform: "capitalize" }}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Dropdown.Button
            type="primary"
            overlay={
              <Menu>
                <Menu.Item
                  onClick={() => {
                    setMode("edit");
                    setModalVisible(true);
                    setProduct({
                      ...record,
                      user: {
                        _id: userDetails?._id,
                        name: userDetails?.name,
                        email: userDetails?.email,
                        phone: userDetails?.phone,
                        host: userDetails?.host,
                      },
                    });
                  }}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setCurrentScreen({
                      name: "sales",
                      data: {
                        product: record,
                        user: userDetails,
                      },
                    });
                  }}
                >
                  View Sales
                </Menu.Item>
              </Menu>
            }
          />
        </Space>
      ),
    },
  ];

  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = data.findIndex((x) => x.index === restProps["data-row-key"]);
    return <SortableItem index={index} {...restProps} />;
  };

  return (
    <>
      <Space direction="vertical">
        <Space direction="vertical">
          <Typography.Text strong>GST Details:</Typography.Text>
          {userDetails?.gstNumber ? (
            <>
              <Typography>{userDetails?.gstNumber}</Typography>
              <Typography>
                <strong>GST Registration Name:</strong>{" "}
                {userDetails?.gstRegistrationName}
              </Typography>
              <Typography>
                <strong>GST Registration Address:</strong>{" "}
                {userDetails?.gstRegistrationAddress}
              </Typography>
              <Typography>
                <strong>GST State:</strong> {userDetails?.gstState}
              </Typography>
            </>
          ) : (
            <Typography>N/A</Typography>
          )}
        </Space>
        <Space direction="vertical">
          <Typography.Text strong>Bank Details:</Typography.Text>
          {!userDetails?.accountNumber &&
          !userDetails?.internationalBankAccount ? (
            <Typography>N/A</Typography>
          ) : null}
          {userDetails?.accountNumber ? (
            <>
              <Typography>
                <strong>Account Number:</strong> {userDetails?.accountNumber}
              </Typography>
              <Typography>
                <strong>IFSC:</strong> {userDetails?.ifsc}
              </Typography>
              <Typography>
                <strong>Bank Name:</strong> {userDetails?.bankName}
              </Typography>
            </>
          ) : null}
          {userDetails?.internationalBankAccount ? (
            <>
              <Typography>
                <strong>Account Number: </strong>{" "}
                {userDetails?.internationalBankAccount?.account_number}
              </Typography>
              <Typography>
                <strong>Bank Name: </strong>{" "}
                {userDetails?.internationalBankAccount?.bank_name}
              </Typography>
              <Typography>
                <strong>Country: </strong>
                {userDetails?.internationalBankAccount?.country}
              </Typography>
            </>
          ) : null}
        </Space>
      </Space>

      <Divider />

      <Button
        type="primary"
        disabled={userDetails?.isReferEarnSuspended}
        onClick={() => {
          setMode("add");
          setModalVisible(true);
          setProduct({
            user: {
              _id: userDetails?._id,
              name: userDetails?.name,
              email: userDetails?.email,
              phone: userDetails?.phone,
              host: userDetails?.host,
            },
          });
        }}
      >
        Add Product
      </Button>

      <Divider />

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={false}
        rowKey="index"
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
        expandable={{
          expandedRowRender: (record) => (
            <RefernEarnPartnerSalesAnalyticsExpand productId={record?._id} />
          ),
        }}
      />

      <RefernEarnAddEditProductModal
        mode={mode}
        product={product}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSave={() => {
          handleGetRefernEarnPartnerProducts();
          setModalVisible(false);
        }}
      />
    </>
  );
};

RefernEarnPartnerDetailsExpand.propTypes = {
  userDetails: PropTypes.shape(),
  setCurrentScreen: PropTypes.func.isRequired,
};

export default RefernEarnPartnerDetailsExpand;
